import React from 'react';
import { LanguageId, SupportedLanguages } from '~domain/model/core/supported-languages';
import { changeAppLanguage } from '~frontend-common/helper/application/change-app-language';
import { useTranslator } from '~frontend-common/i18n/translator';
import { NavbarButton } from './navbar-button';

interface LanguageToggleButtonProps {
  disabled?: boolean;
  onBeforeLanguageChange?: (language: LanguageId) => Promise<void>;
}

export function LanguageToggleButton({ disabled, onBeforeLanguageChange }: LanguageToggleButtonProps) {
  const translator = useTranslator();
  const { t } = translator;

  // Get language element to tag toggle button in the status bar
  const getToggleLangId = (): LanguageId => {
    const currentLangId = translator.getCurrentLanguageId();

    // If language is not supported, our toggle element is French
    if (!translator.isLanguageSupported(currentLangId)) {
      return SupportedLanguages[1].id;
    }

    // Remove current language from list of languages
    const otherLanguages = SupportedLanguages.filter((lng) => lng.id !== currentLangId);

    // If there are no more languages in array, return current language
    if (otherLanguages.length === 0) {
      return currentLangId;
    }

    // Return the first language that is not the current one
    return otherLanguages[0].id;
  };

  const targetLangId = getToggleLangId();

  const onChangeLanguage = async () => {
    await onBeforeLanguageChange?.(targetLangId);
    await changeAppLanguage(targetLangId, translator);
  };

  return <NavbarButton label={t(`languages.${targetLangId}`)} disabled={disabled} onClick={onChangeLanguage} />;
}
